import React, { useState, useEffect } from "react";
import { Link, useLocation } from "react-router-dom";

// Navigation links configuration
const navLinks = [
  { to: "/", label: "Home" },
  { to: "/about", label: "About" },
  { to: "/try", label: "Try Us Now" },
  { to: "/contact", label: "Contact" },
];

/**
 * Header: Fixed navigation bar with responsive mobile menu
 * - Changes appearance when scrolled (adds shadow/background)
 * - Highlights active route
 * - Mobile hamburger menu
 */
const Header: React.FC = () => {
  // useLocation hook: gets current route path to highlight active link
  const location = useLocation();
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [isScrolled, setIsScrolled] = useState(false);

  // Track scroll position to change header styling
  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 20);
    };
    window.addEventListener('scroll', handleScroll);
    // Cleanup: remove listener when component unmounts
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  return (
    <>
      <header className={`fixed top-0 z-50 w-full transition-all duration-300 ${
        isScrolled
          ? 'bg-white/95 backdrop-blur-xl shadow-lg border-b border-gray-100'
          : 'bg-white/80 backdrop-blur-md'
      }`}>
        <nav className="container-max flex items-center justify-between px-4 sm:px-6 py-4">
          {/* Logo */}
          <div className="flex items-center">
            <Link to="/" className="flex items-center group">
              <img 
                src="/images/logoV2.png" 
                alt="ReadyToClaim Logo" 
                className="h-16 w-auto group-hover:scale-105 transition-transform duration-200"
              />
            </Link>
          </div>

          {/* Desktop Navigation - hidden on mobile, visible on md+ screens */}
          <div className="hidden md:flex items-center space-x-1">
            {navLinks.map((link) => (
              <Link
                key={link.to}
                to={link.to}
                // Highlight active route with different styling
                className={`px-4 py-2 rounded-xl font-medium transition-all duration-200 ${
                  location.pathname === link.to
                    ? 'text-primary-600 bg-primary-50'  // Active route
                    : 'text-gray-600 hover:text-gray-900 hover:bg-gray-50'  // Inactive route
                }`}
              >
                {link.label}
              </Link>
            ))}
            <Link
              to="https://app.readytoclaim.com"
              className="btn-primary ml-4"
            >
              Login
            </Link>
          </div>

          {/* Mobile menu button */}
          <div className="md:hidden">
            <button
              onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
              className="p-2 rounded-xl text-gray-600 hover:text-gray-900 hover:bg-gray-50 transition-all duration-200 focus:outline-none focus:ring-2 focus:ring-primary-200"
              aria-label="Toggle menu"
            >
              <svg className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                {isMobileMenuOpen ? (
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                ) : (
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16M4 18h16" />
                )}
              </svg>
            </button>
          </div>
        </nav>

        {/* Mobile Navigation */}
        {isMobileMenuOpen && (
          <div className="md:hidden bg-white border-t border-gray-100 animate-slide-up">
            <div className="px-4 py-4 space-y-2">
              {navLinks.map((link) => (
                <Link
                  key={link.to}
                  to={link.to}
                  className={`block px-4 py-3 rounded-xl font-medium transition-all duration-200 ${
                    location.pathname === link.to
                      ? 'text-primary-600 bg-primary-50'
                      : 'text-gray-600 hover:text-gray-900 hover:bg-gray-50'
                  }`}
                  onClick={() => setIsMobileMenuOpen(false)}
                >
                  {link.label}
                </Link>
              ))}
                      <Link
                        to="https://app.readytoclaim.com"
                        className="block px-4 py-3 mt-4 bg-primary-600 text-white font-semibold rounded-xl hover:bg-primary-700 transition-colors duration-200"
                        onClick={() => setIsMobileMenuOpen(false)}
                      >
                        Login
                      </Link>
            </div>
          </div>
        )}
      </header>
      <div className="h-16 md:h-20" aria-hidden="true"></div>
    </>
  );
};

export default Header; 